import type { GameState } from '../state';
import { D, add, sub, mul, gte, pow, max, type Decimal } from '../math/num';
import {
  DIVISIONS,
  getDivision,
  MAX_STAKE,
  BUYOUT_COMPONENT_BONUS,
  BUYOUT_YIELD,
  DIVIDEND_MS,
  DIVIDEND_RATE,
  SHIFT_COOLDOWN_MS,
  STAKE_COST_MULT,
  DRIFT_A1,
  DRIFT_P1_MS,
  DRIFT_A2,
  DRIFT_P2_MS,
} from '../data/takeover';
import { waveScrap } from './spawn';

/**
 * Hostile Takeover sim (data/takeover.ts holds the numbers). Yields are pure
 * functions of (save seed, elapsed time) — deterministic forever, no market
 * state, identical online and offline.
 */

export interface TakeoverOpResult {
  ok: boolean;
  reason?: string;
}

/** Stable per-division phase derived from the save seed (no RNG draws). */
function phase(seed: number, salt: number): number {
  let h = (seed ^ (salt * 0x9e3779b9)) | 0;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h >>> 0) / 4294967296) * Math.PI * 2;
}

/** A division's live yield factor at a moment (bought-out = fixed blue chip). */
export function divisionYield(state: GameState, divId: string): number {
  const def = getDivision(divId);
  if (!def) return 0;
  if ((state.takeover.stakes[divId] ?? 0) >= MAX_STAKE) return BUYOUT_YIELD;
  const i = DIVISIONS.findIndex((d) => d.id === divId);
  const t = state.elapsedMs;
  const drift =
    DRIFT_A1 * Math.sin((Math.PI * 2 * t) / DRIFT_P1_MS + phase(state.rng.seed, i * 2 + 1)) +
    DRIFT_A2 * Math.sin((Math.PI * 2 * t) / DRIFT_P2_MS + phase(state.rng.seed, i * 2 + 2));
  return Math.max(0.3, def.base * (1 + drift));
}

/** Frontier-anchored Components value of one dividend point (the boss-kill formula). */
function componentAnchor(state: GameState): Decimal {
  return max(D(1), pow(D(1.08), state.combat.highestWave - 1));
}

/** Next stake level's Scrap price (steeper each level, anchored to frontier). */
export function stakeCost(state: GameState, divId: string): Decimal | null {
  const level = state.takeover.stakes[divId] ?? 0;
  if (level >= MAX_STAKE) return null;
  return mul(waveScrap(state.combat.highestWave), D(STAKE_COST_MULT * (level + 1) * (level + 1)));
}

export function buyStake(state: GameState, divId: string): TakeoverOpResult {
  const tk = state.takeover;
  if (!tk.unlocked) return { ok: false, reason: 'The desk is not open.' };
  if (!getDivision(divId)) return { ok: false, reason: 'Unknown division.' };
  const level = tk.stakes[divId] ?? 0;
  if (level >= MAX_STAKE) return { ok: false, reason: 'Already bought out.' };
  const cost = stakeCost(state, divId)!;
  if (!gte(state.wallet.scrap, cost)) return { ok: false, reason: 'Not enough Scrap.' };
  state.wallet.scrap = sub(state.wallet.scrap, cost);
  tk.stakes[divId] = level + 1;
  return { ok: true };
}

/** Move ONE stake level between divisions, free, once per hour. Buyouts are
 *  permanent; you cannot strip a bought-out division. */
export function shiftStake(state: GameState, fromId: string, toId: string): TakeoverOpResult {
  const tk = state.takeover;
  if (!tk.unlocked) return { ok: false, reason: 'The desk is not open.' };
  if (fromId === toId) return { ok: false, reason: 'That is not a move.' };
  if (!getDivision(fromId) || !getDivision(toId)) return { ok: false, reason: 'Unknown division.' };
  if (state.elapsedMs - tk.lastShiftMs < SHIFT_COOLDOWN_MS) return { ok: false, reason: 'The board allows one reallocation per hour.' };
  const from = tk.stakes[fromId] ?? 0;
  const to = tk.stakes[toId] ?? 0;
  if (from <= 0) return { ok: false, reason: 'Nothing staked there.' };
  if (from >= MAX_STAKE) return { ok: false, reason: 'Buyouts are forever. MechCorp wrote the contracts.' };
  if (to >= MAX_STAKE) return { ok: false, reason: 'That division is already bought out.' };
  tk.stakes[fromId] = from - 1;
  tk.stakes[toId] = to + 1;
  tk.lastShiftMs = state.elapsedMs;
  return { ok: true };
}

/** Total dividend per payout at a moment (display + the step payout). */
export function dividendPerPayout(state: GameState): Decimal {
  let points = 0;
  for (const d of DIVISIONS) {
    const level = state.takeover.stakes[d.id] ?? 0;
    if (level > 0) points += level * divisionYield(state, d.id);
  }
  if (points <= 0) return D(0);
  return mul(componentAnchor(state), D(points * DIVIDEND_RATE));
}

/** Pay dividends on schedule (called from stepWorld). */
export function stepTakeover(state: GameState, _dtMs: number): void {
  const tk = state.takeover;
  if (!tk.unlocked) return;
  if (tk.nextPayoutMs === 0) {
    tk.nextPayoutMs = state.elapsedMs + DIVIDEND_MS;
    return;
  }
  while (state.elapsedMs >= tk.nextPayoutMs) {
    const pay = dividendPerPayout(state);
    if (pay.gt(0)) {
      state.wallet.components = add(state.wallet.components, pay);
      tk.lastPayout = pay;
      tk.payouts += 1;
    }
    tk.nextPayoutMs += DIVIDEND_MS;
  }
}

/** Buyouts (stake 10) each grant a permanent Components multiplier. */
export function takeoverComponentBonus(state: GameState): Decimal {
  if (!state.takeover.unlocked) return D(1);
  let buyouts = 0;
  for (const d of DIVISIONS) if ((state.takeover.stakes[d.id] ?? 0) >= MAX_STAKE) buyouts += 1;
  return D(1 + BUYOUT_COMPONENT_BONUS * buyouts);
}

/** Open the dividends desk — rides the MechCorp HQ kill. */
export function unlockTakeover(state: GameState): void {
  if (state.takeover.unlocked) return;
  state.takeover.unlocked = true;
  state.meta.unlocks.takeover = true;
}
